import _ from 'lodash';
import { watch } from 'vue-demi';

import { CallInfo, ConferenceMember, CALL_MEMBER_STATUS } from '../types';
import { useDialerStore, useDialerGetters, useDialerSetters } from './use-dialer-store';
import { useCommonProps } from './use-common-props';
import { useConferenceMembersActions } from './use-conference-members-actions';
import { STEP_EVENTS } from '../constants';

/**
 * Due to delayed initialization, destructuring is limited,
 * so we can preserve references when actual values become available.
 */
export function useDialerActions() {
  const props = useCommonProps();
  const store = useDialerStore();
  const getters = useDialerGetters();
  const setters = useDialerSetters();

  const initializeVoiceAPI = async () => {
    const api = await props.initializeVoiceAPI({
      onIncomingCall,
      onJoinCall,
      onLeaveCall,
    });

    props.setVoiceAPI(api);
    store.isVoiceAPIReady = true;
  };

  /** Can trigger call popup, but doesn't have enough info to display all features */
  const onIncomingCall = async (args: { number: string; }) => {
    setters.onIncomingCall(args.number);
    await answer();
  };

  /**
   * Receive info required to display a full popup will controls.
   * Also answers the call if there was no "onIncomingCall" event.
   */
  const onStartCall = async (args: { info: CallInfo; sessionId: string; }) => {
    setters.onStartCall(args);
    if (store.isRinging && !store.isInProgress) {
      return answer();
    }
  };

  const onJoinCall = () => setters.onJoinCall();
  const onLeaveCall = () => setters.onLeaveCall();
  const playDTMF = async (tone: string) => await props.getVoiceAPI().dtmf(tone);

  const onHangUp = async () => {
    store.isHangUpActionLoading = true;
    await props.getVoiceAPI().hangup(store.sessionId);

    createEventWithLabel(STEP_EVENTS.AGENT_HUNG_UP_CALL);
    store.isHangUpActionLoading = false;
  };

  const onUpsertConferenceMember = async (args: {
    member: ConferenceMember;
  }) => {
    const { member } = args;
    const { emulateSoftPhone } = store.controls;
    const isThisAgentEvent = member.memberId === props.agentId.value;

    const isAgentJoined = [
      CALL_MEMBER_STATUS.JOINED,
      CALL_MEMBER_STATUS.CONNECTED,
    ].includes(member.status);

    const isAgentRemoved = [
      CALL_MEMBER_STATUS.BUSY,
      CALL_MEMBER_STATUS.DISCONNECTED,
      CALL_MEMBER_STATUS.ERROR,
      CALL_MEMBER_STATUS.HANG_UP,
      CALL_MEMBER_STATUS.NOT_CONNECTED,
    ].includes(member.status);

    /* If current agent experienced an error, try to end the call */
    if (isThisAgentEvent && isAgentRemoved) {
      createEventWithLabel(STEP_EVENTS.AGENT_HUNG_UP_CALL);
      setters.onLeaveCall();

      return;
    }

    /* Softphone calls are initialized using flow events */
    if (isThisAgentEvent && isAgentJoined && emulateSoftPhone) {
      props.getVoiceAPI().switch('softphone');
      /* TODO: Can we move this to "onStartCall"? */
      await answer();
    }

    /* TODO: Might not work properly when multiple contacts are supported */
    const isContactEvent = member.memberId === store.contactId;
    if (isContactEvent) {
      const isOnHold = member.status === CALL_MEMBER_STATUS.ON_HOLD;
      store.isContactOnHold = isOnHold;
    }

    useConferenceMembersActions().onUpsertConferenceMember(args);
  };

  /** Answering the call enables all features and controls for the agent */
  const answer = async () => {
    try {
      props.Ringtone.stop();
      store.isAnswering = true;

      await props.getVoiceAPI().answer();
      if (store.isAgentMuted) {
        onMuteSelf(true);
      }

      setters.answer();
      createEventWithLabel(STEP_EVENTS.AGENT_ANSWERED_CALL);
    } catch (e) {
      console.error('Failed to answer call!', e);
      store.isAnswering = false;
    }
  };

  const onMuteSelf = (muteAgent: boolean) => {
    if (!store.controls.mute) {
      return;
    }

    muteAgent ? props.getVoiceAPI().mute() : props.getVoiceAPI().unmute();
    store.isAgentMuted = muteAgent;

    const label = muteAgent
      ? STEP_EVENTS.AGENT_MUTED_CALL
      : STEP_EVENTS.AGENT_UNMUTED_CALL;
    createEventWithLabel(label);
  };

  const onToggleMuteSelf = () => {
    if (store.controls.mute) {
      onMuteSelf(!store.isAgentMuted);
    }
  };

  /** "Hold" action is a command, rather then a Voice API feature */
  const onPutOnHold = async (putOnHold: boolean) => {
    const HOLD_COMMAND = 'hold';
    const UNHOLD_COMMAND = 'unhold';

    try {
      store.isPutOnHoldActionLoading = true;

      await props.applyCommand({
        sessionId: store.sessionId,
        command: putOnHold ? HOLD_COMMAND : UNHOLD_COMMAND,
        data: {
          hold: putOnHold,
        },
        throwError: true,
      });

      store.isContactOnHold = putOnHold;

      const label = putOnHold
        ? STEP_EVENTS.AGENT_PUT_CALL_ON_HOLD
        : STEP_EVENTS.AGENT_PUT_CALL_OFF_HOLD;
      createEventWithLabel(label);
    } catch (e) {
      console.error('Failed to apply "Put Call On Hold" command!', e);
    } finally {
      store.isPutOnHoldActionLoading = false;
    }
  };

  const onTogglePutOnHold = () => {
    if (store.controls.hold) {
      onPutOnHold(!store.isContactOnHold);
    }
  };

  /** Before "answer" is complete, call can still be rejected by user */
  const cancelWaitingForAnswer = async () => {
    store.isWaiting = false;
    if (store.isRinging && !store.isInProgress) {
      await reject();
    }
  };

  const reject = async () => {
    try {
      store.isHangUpActionLoading = true;

      await props.getVoiceAPI().decline();
      createEventWithLabel(STEP_EVENTS.AGENT_REJECTED_CALL);
    } finally {
      store.isHangUpActionLoading = false;
    }
  };

  /** Some events for the timeline are generated and saved by UI */
  const createEventWithLabel = (label: string) => {
    if (store.sessionId) {
      props.createEvent({ sessionId: store.sessionId, label });
    }
  };

  const onMembersChange = (members: ConferenceMember[]) => {
    if (!getters.isDialerVisible.value) {
      return;
    }

    props.Ringtone.stop();
    const additional = _.reject(members, { memberId: props.agentId.value });

    if (_.some(additional, { status: CALL_MEMBER_STATUS.RINGING })) {
      props.Ringtone.play('rbt');
    }
  };

  /** Play ringing sound when one of the members is connecting */
  const subscribeRingtone = () => {
    watch(getters.members, onMembersChange, { immediate: true, deep: true });
    watch(getters.isDialerVisible, (isVisible: boolean) => {
      if (!isVisible) {
        props.Ringtone.stop();
      }
    });
  };

  /** Go to relevant session when call starts */
  const subscribeToIsVisible = () => {
    watch(getters.isDialerVisible, (current, previous) => {
      const isVisible = current && !previous;
      const isSameSession = store.sessionId === props.activeSessionId.value;

      if (isVisible && !isSameSession) {
        props.goToSession({ sessionId: store.sessionId });
      }
    });
  };

  return {
    answer,
    cancelWaitingForAnswer,
    initializeVoiceAPI,
    onHangUp,
    onIncomingCall,
    onJoinCall,
    onLeaveCall,
    onMuteSelf,
    onStartCall,
    onToggleMuteSelf,
    onTogglePutOnHold,
    onUpsertConferenceMember,
    playDTMF,
    reject,
    subscribeRingtone,
    subscribeToIsVisible,
  };
}
